import { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { formatDate } from "helpers/dateTimeHelper";

import { convertCurrency } from "api";
import errorToast from "helpers/errorToast";

interface Charge {
  currency_origin: string;
  target_currency_code: string;
}

interface CurrencyPair {
  origin: string;
  target: string;
}

interface ChargeGroup {
  [key: string]: Charge[];
}

const getUniqueCurrencyPairs = (data: ChargeGroup): { origin: string; target: string }[] => {
  const uniquePairs = new Set<string>();

  Object.values(data).forEach((charges) => {
    charges.forEach(({ currency_origin, target_currency_code }) => {
      if (currency_origin !== target_currency_code) uniquePairs.add(`${currency_origin}-${target_currency_code}`);
    });
  });

  return Array.from(uniquePairs).map((pair) => {
    const [origin, target] = pair.split("-");
    return { origin, target };
  });
};

function CostConversionRates({ costDetailsData, v2Format = false }: { costDetailsData: any; v2Format?: boolean }) {
  const [currencyRates, setCurrencyRates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchCurrencyRates();
  }, []);

  const fetchCurrencyRates = () => {
    const currencyPairs = getUniqueCurrencyPairs(costDetailsData.costs.quotation_cost_items);
    if (currencyPairs.length === 0) return;
    setIsLoading(true);

    Promise.all(
      currencyPairs.map((pair: CurrencyPair) =>
        convertCurrency({
          params: {
            currency: pair.origin,
            new_currency: pair.target,
            value: 1,
          },
        })
          .then((res) => ({ ...pair, rate: res.data.conversion_rate, timestamp: res.data.last_updated }))
          .catch((err) => errorToast(err)),
      ),
    )
      .then((results) => {
        const validResults = results.filter((result) => result !== null);
        setCurrencyRates(validResults);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="w-100 text-right font-b3 text-secondary">
      {isLoading && (
        <FormattedMessage id="quote.costDetails.fetchingExchangeRates" defaultMessage="Fetching exchange rates..." />
      )}
      {!isLoading &&
        currencyRates.map((rate) => (
          <div key={`${rate.origin}-${rate.target}`}>
            {!v2Format ? (
              <>
                <FormattedMessage
                  id="quote.costDetails.conversionRate"
                  defaultMessage="Conversion rate 1 {origin} is {rate} {target} ({date} UTC)"
                  values={{
                    origin: rate.origin,
                    rate: rate.rate,
                    target: rate.target,
                  }}
                />
                ({formatDate(rate.timestamp, "DD MMM YYYY - HH:mm")} UTC)
              </>
            ) : (
              <FormattedMessage
                id="quote.costDetails.conversionRateV2"
                defaultMessage="Conversion rate as of {date} UTC: 1 {origin} = {rate} {target}"
                values={{
                  origin: rate.origin,
                  rate: rate.rate,
                  target: rate.target,
                  date: formatDate(rate.timestamp, "DD MMM YYYY - HH:mm"),
                }}
              />
            )}
          </div>
        ))}
    </div>
  );
}

export default CostConversionRates;

CostConversionRates.defaultProps = {
  v2Format: false,
};
